/**
 * Module dependencies.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Select, Input, message, Spin } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import { Title } from 'app/components/atoms/title/title';
import { Button } from 'app/components/atoms/button/button';
import { useTranslation } from 'react-i18next';
import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';
import { resolveAppUrl } from 'app/core/utils/url-resolver';
import type { User } from 'app/types/user';
import styles from './sms-test.module.less';

const { TextArea } = Input;

/**
 * Export `SmsTest` component.
 */

export function SmsTest(): JSX.Element {
  const [translate] = useTranslation();
  const client = useRequestProvider();

  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>();
  const [smsMessage, setSmsMessage] = useState('');
  const [sending, setSending] = useState(false);

  const fetchDeveloperUsers = useCallback(async () => {
    setLoadingUsers(true);

    try {
      const url = resolveAppUrl('developerUsers');
      const response = await client.get(url);
      const responseData = response.data?.data;
      const data = Array.isArray(responseData?.results)
        ? responseData.results
        : Array.isArray(responseData)
        ? responseData
        : [];

      setUsers(data);
    } catch (error) {
      console.error('Failed to fetch developer users:', error);
      message.error(translate('developer.smsTest.errorFetchingUsers'));
    } finally {
      setLoadingUsers(false);
    }
  }, [client, translate]);

  useEffect(() => {
    fetchDeveloperUsers();
  }, [fetchDeveloperUsers]);

  const handleSendSms = async () => {
    if (!selectedUserId) {
      message.warning(translate('developer.smsTest.selectUserWarning'));

      return;
    }

    if (!smsMessage.trim()) {
      message.warning(translate('developer.smsTest.emptyMessageWarning'));

      return;
    }

    setSending(true);

    try {
      const url = resolveAppUrl('smsTest');

      await client.post(url, {
        userId: selectedUserId,
        message: smsMessage
      });

      message.success(translate('developer.smsTest.smsSentSuccess'));
      setSmsMessage('');
    } catch (error: any) {
      console.error('Failed to send SMS:', error);
      const errorMessage = error?.response?.data?.message || translate('developer.smsTest.errorSendingSms');

      message.error(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const selectedUser = Array.isArray(users) ? users.find(user => user.id === selectedUserId) : undefined;

  return (
    <Card>
      <Title>{translate('developer.smsTest.title')}</Title>

      <div className={styles.container}>
        <div className={styles.field}>
          <label className={styles.label}>{translate('developer.smsTest.selectUser')}</label>

          <Select
            className={styles.select}
            disabled={loadingUsers}
            filterOption={(input, option) =>
              (option?.label?.toString().toLowerCase() ?? '').includes(input.toLowerCase())
            }
            loading={loadingUsers}
            notFoundContent={loadingUsers ? <Spin size={'small'} /> : translate('developer.smsTest.noUsersFound')}
            onChange={value => setSelectedUserId(value)}
            options={(Array.isArray(users) ? users : []).map(user => ({
              value: user.id,
              label: `${user.name} (${user.phoneNumber || 'No phone'})`
            }))}
            placeholder={translate('developer.smsTest.selectUserPlaceholder')}
            showSearch
            value={selectedUserId}
          />
        </div>

        {selectedUser && (
          <div className={styles.userInfo}>
            <span className={styles.infoLabel}>
              {translate('developer.smsTest.phoneNumber')}
              {':'}
            </span>
            <span className={styles.infoValue}>{selectedUser.phoneNumber || 'N/A'}</span>
          </div>
        )}

        <div className={styles.field}>
          <label className={styles.label}>{translate('developer.smsTest.message')}</label>

          <TextArea
            disabled={sending}
            maxLength={320}
            onChange={e => setSmsMessage(e.target.value)}
            placeholder={translate('developer.smsTest.messagePlaceholder')}
            rows={4}
            showCount
            value={smsMessage}
          />
        </div>

        <div className={styles.actions}>
          <Button
            disabled={!selectedUserId || !smsMessage.trim() || !selectedUser?.phoneNumber}
            icon={<SendOutlined />}
            loading={sending}
            onClick={handleSendSms}
            type={'primary'}
          >
            {translate('developer.smsTest.sendButton')}
          </Button>
        </div>

        {selectedUser && !selectedUser.phoneNumber && (
          <div className={styles.warning}>{translate('developer.smsTest.noPhoneWarning')}</div>
        )}
      </div>
    </Card>
  );
}
